import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { GameGalleryPhoto } from "@/types/game-gallery";
import { Icon } from "@/components/ui/icon"

async function downloadMedia(url: string, filename: string) {
  try {
    const response = await fetch(url, { mode: "cors" });
    if (!response.ok) throw new Error("fetch failed");
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(objectUrl);
  } catch {
    const anchor = document.createElement("a");
    anchor.href = url.includes("dropbox") ? `${url.split("?")[0]}?dl=1` : url;
    anchor.download = filename;
    anchor.target = "_blank";
    anchor.rel = "noopener noreferrer";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
  }
}

function getDownloadFilename(photo: GameGalleryPhoto, index: number) {
  const isVideo = photo.mediaType === "VIDEO";
  return isVideo ? `jogo-lance-${index + 1}.mp4` : `jogo-foto-${index + 1}.jpg`;
}

interface GameGalleryMediaViewerProps {
  photo: GameGalleryPhoto;
  open: boolean;
  onClose: () => void;
  subtitle?: string;
  footer?: ReactNode;
}

export function GameGalleryMediaViewer({
  photo,
  open,
  onClose,
  subtitle,
  footer,
}: GameGalleryMediaViewerProps) {
  const isVideo = photo.mediaType === "VIDEO";
  const itemLabel = isVideo ? "lance" : "foto";

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-black"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Visualização do ${itemLabel}`}
    >
      <div
        className="flex shrink-0 items-center justify-between gap-3 px-4 pb-3 pt-[max(0.75rem,env(safe-area-inset-top))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="min-w-0 flex-1">
          {subtitle ? (
            <p className="truncate text-sm font-semibold text-white">{subtitle}</p>
          ) : null}
          <p className="truncate text-xs text-white/70">{photo.userName}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20"
          aria-label="Fechar"
        >
          <Icon name="close" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2 pb-2">
        {isVideo ? (
          <video
            src={photo.url}
            controls
            autoPlay
            playsInline
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        ) : (
          <img
            src={photo.url}
            alt={`Foto do jogo por ${photo.userName}`}
            className="max-h-full max-w-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>

      {footer ? (
        <div
          className="flex shrink-0 flex-wrap items-center justify-center gap-2 px-4 pb-[max(1.5rem,env(safe-area-inset-bottom))] pt-2"
          onClick={(e) => e.stopPropagation()}
        >
          {footer}
        </div>
      ) : null}
    </div>,
    document.body,
  );
}

export function GameGalleryMediaViewerDownloadButton({
  photo,
  index = 0,
  onError,
}: {
  photo: GameGalleryPhoto;
  index?: number;
  onError: (message: string) => void;
}) {
  const isVideo = photo.mediaType === "VIDEO";
  const itemLabel = isVideo ? "lance" : "foto";
  const downloadName = getDownloadFilename(photo, index);

  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={async () => {
        try {
          await downloadMedia(photo.url, downloadName);
        } catch {
          onError(`Não foi possível baixar o ${itemLabel}`);
        }
      }}
      className="gap-1.5"
    >
      <Icon name="download" />
      Baixar
    </Button>
  );
}

export function GameGalleryMediaViewerLikeButton({
  photo,
  onLike,
  isLikePending,
}: {
  photo: GameGalleryPhoto;
  onLike: () => void;
  isLikePending?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="sm"
      onClick={onLike}
      disabled={isLikePending}
      className="gap-1.5"
    >
      <Icon name="favorite" filled className={cn(photo.likedByMe && "material-icon-filled text-red-500")} />
      {photo.likedByMe ? "Descurtir" : photo.likesCount > 0 ? String(photo.likesCount) : "Curtir"}
    </Button>
  );
}
